import React, { useEffect, useState } from 'react'
import { Alert, Button, ButtonGroup, Form, Nav, Spinner } from 'react-bootstrap'
import { NavLink, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { signOut } from 'aws-amplify/auth'
import {
  fetchInvestments,
  createInvestment,
  updateInvestment,
  deleteInvestment,
} from '../../../reducers/investmentsSlice'
import InvestmentTable from '../../../components/InvestmentTable'
import InvestmentForm from '../../../components/InvestmentForm'
import InvestmentSaleModal from '../../../components/InvestmentSaleModal'
import InvestmentDeleteModal from '../../../components/InvestmentDeleteModal'

function AdminInvestments() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { items, loading, error } = useSelector((state) => state.investments)

  const [activeView, setActiveView] = useState('open')
  const [searchTerm, setSearchTerm] = useState('')
  const [formOpen, setFormOpen] = useState(false)
  const [saleTarget, setSaleTarget] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [saleError, setSaleError] = useState(null)

  useEffect(() => {
    dispatch(fetchInvestments())
  }, [dispatch])

  async function handleSignOut() {
    await signOut()
    navigate('/admin')
  }

  function handleViewChange(view) {
    setActiveView(view)
    setSearchTerm('')
    setFormOpen(false)
  }

  async function handleAddInvestment(data) {
    await dispatch(createInvestment(data)).unwrap()
    dispatch(fetchInvestments())
    setFormOpen(false)
  }

  async function handleRecordSale(data) {
    try {
      await dispatch(
        updateInvestment({ id: saleTarget.id, purchaseDate: saleTarget.purchase_date, data })
      ).unwrap()
      dispatch(fetchInvestments())
      setSaleTarget(null)
      setSaleError(null)
    } catch (err) {
      setSaleError(err.message || 'Failed to record sale.')
    }
  }

  async function handleDelete() {
    await dispatch(
      deleteInvestment({ id: deleteTarget.id, purchaseDate: deleteTarget.purchase_date })
    ).unwrap()
    dispatch(fetchInvestments())
    setDeleteTarget(null)
  }

  const displayRecords = items
    .filter((i) => (activeView === 'open' ? !i.sale_date : !!i.sale_date))
    .filter((i) => {
      if (activeView !== 'closed' || !searchTerm) return true
      const term = searchTerm.toLowerCase()
      return (
        i.symbol.toLowerCase().includes(term) ||
        i.account.toLowerCase().includes(term)
      )
    })
    .sort((a, b) =>
      activeView === 'open'
        ? new Date(b.purchase_date) - new Date(a.purchase_date)
        : new Date(b.sale_date) - new Date(a.sale_date)
    )

  function getEmptyMessage() {
    if (activeView === 'open') return 'No open investments.'
    if (searchTerm) return `No results for "${searchTerm}".`
    return 'No closed investments.'
  }

  return (
    <section className="py-4">
      <header className="mb-4 d-flex justify-content-between align-items-center">
        <div>
          <p
            className="text-uppercase fw-semibold text-muted mb-1"
            style={{ fontSize: '0.7rem', letterSpacing: '0.1em' }}
          >
            Admin
          </p>
          <h2 className="fw-bold mb-0">Investment Tracker</h2>
        </div>
        <Button variant="outline-secondary" size="sm" onClick={handleSignOut}>
          Sign Out
        </Button>
      </header>
      <hr className="opacity-25" />
      <Nav variant="tabs" className="mb-4">
        <Nav.Item>
          <Nav.Link as={NavLink} to="/admin/charities">Charity Manager</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link as={NavLink} to="/admin/sre">SRE Events</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link as={NavLink} to="/admin/stock">Stock Pricing</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link as={NavLink} to="/admin/snp500">S&amp;P 500 List</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link as={NavLink} to="/admin/investments">Investments</Nav.Link>
        </Nav.Item>
      </Nav>

      <div className="mb-3 d-flex align-items-center gap-3 flex-wrap">
        <ButtonGroup size="sm">
          <Button
            variant={activeView === 'open' ? 'primary' : 'outline-primary'}
            onClick={() => handleViewChange('open')}
          >
            Open
          </Button>
          <Button
            variant={activeView === 'closed' ? 'primary' : 'outline-primary'}
            onClick={() => handleViewChange('closed')}
          >
            Closed
          </Button>
        </ButtonGroup>

        {activeView === 'closed' && (
          <Form.Control
            size="sm"
            type="text"
            placeholder="Search by symbol or account…"
            aria-label="Search investments"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ maxWidth: 240 }}
          />
        )}

        {activeView === 'open' && (
          <Button size="sm" variant="success" onClick={() => setFormOpen((v) => !v)}>
            {formOpen ? 'Cancel' : 'Add Investment'}
          </Button>
        )}
      </div>

      {activeView === 'open' && (
        <InvestmentForm
          open={formOpen}
          onSubmit={handleAddInvestment}
          onClose={() => setFormOpen(false)}
        />
      )}

      {loading && (
        <div className="my-3">
          <Spinner animation="border" size="sm" role="status">
            <span className="visually-hidden">Loading…</span>
          </Spinner>
        </div>
      )}

      {error && !loading && (
        <Alert variant="danger">{error}</Alert>
      )}

      {!error && (
        <InvestmentTable
          records={displayRecords}
          activeView={activeView}
          emptyMessage={getEmptyMessage()}
          onRecordSale={setSaleTarget}
          onDelete={setDeleteTarget}
        />
      )}

      {saleTarget && (
        <InvestmentSaleModal
          investment={saleTarget}
          onSubmit={handleRecordSale}
          onCancel={() => { setSaleTarget(null); setSaleError(null) }}
          error={saleError}
        />
      )}

      {deleteTarget && (
        <InvestmentDeleteModal
          investment={deleteTarget}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </section>
  )
}

export default AdminInvestments
